import {AdvertiserDevicesModel} from './advertiser-devices.model';
import {Action, State, StateContext, Store} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {
  AddToFavoriteDevice, CleanAdvertiserDevicesStore, GetAdvertiserAllDevices,
  GetAdvertiserDevicesBySubmit, RemoveFromFavoriteDevices,
  SearchAdvertiserDevices, SetAdvertiserDevicesPage,
  SetAdvertiserMapBounds, SortAdvertiserDevices
} from './advertiser-devices.actions';
import {Observable, of} from 'rxjs';
import {IResponse} from '@interfaces/response';
import {IDeviceAdvertiser} from '@interfaces/advertiser/devices';
import {AdvertiserDevicesService} from '../../services-advertiser/devices/devices.service';
import {filter, tap} from 'rxjs/operators';
import {removeEmptyPropsFromObj} from '@shared/functions';
import {RouterSelectors} from '@store/router/router.selectors';
import {HttpResponseBase} from '@angular/common/http';

@State<AdvertiserDevicesModel>({
  name: 'advertiserDevicesState',
  defaults: {
    mapBounds: null,
    devices: null,
    searchParam: '',
    industry: '',
    orientation: null,
    priceFrom: null,
    priceTo: null,
    devicesConfig: {itemsPerPage: 7, currentPage: 1, sort: 'id', order: 'DESC', isLoading: false}
  }
})

@Injectable()
export class AdvertiserDevicesState {
  constructor(private deviceService: AdvertiserDevicesService,
              private store: Store) {
  }

  @Action(SetAdvertiserMapBounds)
  setAdvertiserMapBounds(ctx: StateContext<AdvertiserDevicesModel>, {mapBounds}: SetAdvertiserMapBounds): Observable<any> {
    ctx.patchState({mapBounds});
    return ctx.dispatch(new GetAdvertiserAllDevices());
  }

  @Action(GetAdvertiserAllDevices, {cancelUncompleted: true})
  getAdvertiserAllDevices(ctx: StateContext<AdvertiserDevicesModel>): Observable<IResponse<IDeviceAdvertiser> | null> {
    const bounds = ctx.getState().mapBounds;
    if (!bounds) return of(null);

    const search = ctx.getState().searchParam;
    const priceFrom = ctx.getState().priceFrom;
    const priceTo = ctx.getState().priceTo;
    const orientationType = ctx.getState().orientation;
    const industry = ctx.getState().industry;

    const limit = ctx.getState().devicesConfig.itemsPerPage;
    const skip = limit * (ctx.getState().devicesConfig.currentPage - 1);
    const order = ctx.getState().devicesConfig.order;
    const sort = ctx.getState().devicesConfig.sort;

    const filteredProps = removeEmptyPropsFromObj({priceFrom, priceTo, orientationType, industry});

    ctx.patchState({devicesConfig: {...ctx.getState().devicesConfig, isLoading: true}});
    const mapRequest = this.deviceService.getAdvertiserDevices({
      lngMax: bounds.getEast(), lngMin: bounds.getWest(),
      latMin: bounds.getSouth(), latMax: bounds.getNorth(), search, ...filteredProps
    }).pipe(
      tap((devices) => {
        const state = ctx.getState();
        ctx.setState({...state, devices, devicesConfig: {...state.devicesConfig, isLoading: false}});
      })
    );

    const listRequest = this.deviceService.getAdvertiserDevices({
      lngMax: bounds.getEast(), lngMin: bounds.getWest(),
      latMin: bounds.getSouth(), latMax: bounds.getNorth(), search, ...filteredProps,
      limit, order, sort, skip
    }).pipe(
      tap((devices) => {
        const state = ctx.getState();
        ctx.setState({...state, devices, devicesConfig: {...state.devicesConfig, isLoading: false}});
      })
    );

    const isListPage = this.store.selectSnapshot(RouterSelectors.data).localizeRouter.path === 'list';

    if (isListPage) return listRequest;
    return mapRequest;
  }

  @Action(SearchAdvertiserDevices)
  searchAdvertiserDevices(ctx: StateContext<AdvertiserDevicesModel>, {search}: SearchAdvertiserDevices): Observable<any> {
    ctx.patchState({searchParam: search, devicesConfig: {...ctx.getState().devicesConfig, currentPage: 1}});
    return ctx.dispatch(new GetAdvertiserAllDevices());
  }

  @Action(GetAdvertiserDevicesBySubmit)
  getAdvertiserDevicesBySubmit(ctx: StateContext<AdvertiserDevicesModel>, {payload}: GetAdvertiserDevicesBySubmit): void {
    ctx.setState({
      ...ctx.getState(), priceTo: payload?.priceTo || null,
      priceFrom: payload?.priceFrom || null, orientation: payload?.orientation || null, industry: payload.industry || '',
    });
  }

  @Action(AddToFavoriteDevice)
  addToFavoriteDevice(ctx: StateContext<AdvertiserDevicesModel>,
                      {id}: AddToFavoriteDevice): Observable<HttpResponseBase> {
    return this.deviceService.addToFavorite(id)
      .pipe(
        filter((status) => status.ok),
        tap(() => {
          const devices = ctx.getState().devices?.rows || [];
          const newArr = devices.map((device) => {
            if (device.id === id) {
              return {...device, favorite: true};
            }
            return device;
          });
          ctx.setState({
            ...ctx.getState(),
            devices: {count: ctx.getState().devices?.count || 0, rows: newArr, total: ctx.getState().devices?.total || 0}
          });
        })
      );
  }

  @Action(RemoveFromFavoriteDevices)
  removeFromFavoriteDevices(ctx: StateContext<AdvertiserDevicesModel>,
                            {id}: RemoveFromFavoriteDevices): Observable<HttpResponseBase> {
    return this.deviceService.removeFromFavorite(id)
      .pipe(
        filter((status) => status.ok),
        tap(() => {
          const devices = ctx.getState().devices?.rows || [];
          const newArr = devices.map((device) => {
            if (device.id === id) {
              return {...device, favorite: false};
            }
            return device;
          });
          ctx.setState({
            ...ctx.getState(),
            devices: {count: ctx.getState().devices?.count || 0, rows: newArr, total: ctx.getState().devices?.total || 0}
          });
        })
      );
  }

  @Action(SetAdvertiserDevicesPage)
  setAdvertiserDevicesPage(ctx: StateContext<AdvertiserDevicesModel>, {page}: SetAdvertiserDevicesPage): Observable<any> {
    ctx.patchState({devicesConfig: {...ctx.getState().devicesConfig, currentPage: page}});
    return ctx.dispatch(new GetAdvertiserAllDevices());
  }

  @Action(SortAdvertiserDevices)
  sortAdvertiserDevices(ctx: StateContext<AdvertiserDevicesModel>, {payload}: SortAdvertiserDevices): Observable<any> {
    ctx.patchState({devicesConfig: {...ctx.getState().devicesConfig, sort: payload.sort, order: payload.order}});
    return ctx.dispatch(new GetAdvertiserAllDevices());
  }

  @Action(CleanAdvertiserDevicesStore)
  cleanAdvertiserDevicesStore(ctx: StateContext<AdvertiserDevicesModel>): void {
    ctx.setState({
      mapBounds: null,
      devices: null,
      searchParam: '',
      orientation: null,
      priceFrom: null,
      priceTo: null,
      industry: '',
      devicesConfig: {
        itemsPerPage: ctx.getState().devicesConfig.itemsPerPage, currentPage: 1, sort: 'id', order: 'DESC', isLoading: false}
    });
  }
}
